/**
 *
 *  Organizations/Show/Users
 *
 */

import React, { useState } from 'react'

// NPM Libraries
import PropTypes from 'prop-types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
// import styled from '@emotion/styled'

// GraphQL
import { useMutation, gql } from '@apollo/client'

// Components
import Modal from 'components/Modal'
import UsersSelect from 'components/UsersSelect/WithData'
import UsersTable from 'components/Organizations/Show/Users/Table'
import AdminUsersModal from 'components/Organizations/Show/Users/Admins'

const ADD_USER = gql`
	mutation addUserToOrganization($organization_id: ID!, $user_id: ID!) {
		addUserToOrganization(input: { organization_id: $organization_id, user_id: $user_id }) {
			organization {
				id
				users {
					id
					full_name
					first_name
					last_name
					email
				}
			}
			user {
				full_name
			}
			error
			message
		}
	}
`

const ADD_USERS = gql`
	mutation addUsersToOrganization($organization_id: ID!, $emails: [String!]) {
		addUsersToOrganization(input: { organization_id: $organization_id, emails: $emails }) {
			organization {
				id
				users {
					id
					email
					first_name
					last_name
					full_name
				}
			}
			emails_not_added
		}
	}
`

function AddUserModal({ organization, handleClose }) {
	const [error, setError] = useState(false)

	const [addUsers, { loading }] = useMutation(ADD_USER, {
		onCompleted: ({ addUserToOrganization: { error: _error, message, user } }) => {
			if (_error) {
				setError({ message, user_name: user?.full_name })
			} else {
				setError(false)
			}
		},
	})

	return (
		<Modal
			show
			modalDidClose={handleClose}
			style={{
				width: 600,
			}}
		>
			{loading && (
				<FontAwesomeIcon
					className="float-right text-primary mt-2"
					icon="spinner"
					spin
					style={{ fontSize: '25px' }}
				/>
			)}

			<h3>Add Users To Organization</h3>

			{error && (
				<div className="alert alert-danger">
					<span>
						The User <b>{error.user_name}</b> {error.message}
					</span>
				</div>
			)}

			<UsersSelect
				onSelect={(user) =>
					addUsers({
						variables: { organization_id: organization.id, user_id: user.id },
					})
				}
			/>
			<button className="btn btn-primary float-right mt-3" onClick={handleClose}>
				Done Adding
			</button>
		</Modal>
	)
}

function AddUsersEmailsModal({ organization, handleClose }) {
	const [emails, setEmails] = useState('')
	const [emails_not_added, setEmailsNotAdded] = useState([])

	const [addUsersByEmail, { loading }] = useMutation(ADD_USERS, {
		onCompleted: ({ addUsersToOrganization }) => {
			if (addUsersToOrganization.emails_not_added?.length > 0) {
				setEmailsNotAdded(addUsersToOrganization.emails_not_added)
			} else {
				handleClose()
			}
		},
	})

	return (
		<Modal
			show
			modalDidClose={() => {
				setEmailsNotAdded([])
				handleClose()
			}}
			style={{
				width: 600,
			}}
		>
			<h3>Add Users Emails</h3>

			{!!emails_not_added.length && (
				<div className="alert alert-danger">
					<p className="mb-2">
						<b>The following emails were not found in LeadJig or could not be added to the organization:</b>
					</p>

					<p className="mb-1">
						{emails_not_added.map((email) => (
							<React.Fragment>
								{email}
								<br />
							</React.Fragment>
						))}
					</p>
				</div>
			)}

			<textarea value={emails} onChange={(e) => setEmails(e.target.value)} style={{ width: '100%' }} rows="20" />

			<div className="d-flex justify-content-between mt-2">
				<button className="btn btn-secondary" onClick={() => setEmails('')}>
					Clear List
				</button>

				<button
					className="btn btn-primary"
					onClick={() => {
						const formated_emails = emails
							.replace(/\s+/g, ',')
							.replace(/,+/g, ',')
							.replace(/(^,|,$)/g, '')
							.split(',')
							.filter(Boolean)

						if (formated_emails.length > 0) {
							addUsersByEmail({
								variables: {
									organization_id: organization.id,
									emails: formated_emails,
								},
							})
						}
					}}
					disabled={loading}
				>
					Submit Emails
				</button>
			</div>
		</Modal>
	)
}

function Users({ organization }) {
	const [open_add_users, setOpenAddUsers] = useState(false)
	const [open_add_emails, setOpenAddEmails] = useState(false)
	const [open_admin_users, setOpenAdminUsers] = useState(false)

	return (
		<div>
			<p className="mb-1">
				Create rules and standards that apply to groups of Users. First add advisor-level Users into their
				designated Organization(s). Then adjust settings and content for all Users within the Organization.
				<ul className="pl-4 mb-1">
					<li>There are four types of Organizations: Broker/Dealer, RIA, FMO, and Other.</li>
					<li>
						A single User cannot exist within multiple Organizations of the same type (example: two
						different FMOs).
					</li>
					<li>
						However, Users can belong to multiple Organizations of varying types (example: Broker/Dealer and
						FMO).
					</li>
					<li>
						If a User belongs to multiple Organizations, settings are applied using the following priority:
					</li>
				</ul>
				<ol>
					<li>Broker/Dealer</li>
					<li>RIA</li>
					<li>FMO</li>
					<li>Other</li>
				</ol>
			</p>

			<button type="button" className="btn btn-primary mb-3" onClick={() => setOpenAddUsers(true)}>
				<FontAwesomeIcon icon="plus" /> Select Users to Add
			</button>

			<button type="button" className="btn btn-primary mb-3 ml-2" onClick={() => setOpenAddEmails(true)}>
				<FontAwesomeIcon icon="plus" /> Paste User Emails to Add
			</button>

			<button
				type="button"
				className="btn btn-purple mb-3 ml-2 float-right"
				onClick={() => setOpenAdminUsers(true)}
			>
				<FontAwesomeIcon icon="users-crown" className="mr-1" /> Administrative Users
			</button>

			<UsersTable organization_id={organization.id} />

			{open_add_users && <AddUserModal organization={organization} handleClose={() => setOpenAddUsers(false)} />}

			{open_add_emails && (
				<AddUsersEmailsModal organization={organization} handleClose={() => setOpenAddEmails(false)} />
			)}

			{open_admin_users && (
				<AdminUsersModal organization_id={organization.id} handleClose={() => setOpenAdminUsers(false)} />
			)}
		</div>
	)
}

Users.propTypes = {
	organization: PropTypes.object,
}

AddUserModal.propTypes = {
	organization: PropTypes.object,
	handleClose: PropTypes.func,
}

AddUsersEmailsModal.propTypes = {
	organization: PropTypes.object,
	handleClose: PropTypes.func,
}

export default Users
